// The report's system and return sections on the property page, one card per section of the Solar
// Opportunity Report (10-serverless/120-pack/render.py; Neil, 11 Oct 2026: "the PDF is just an
// export of the sections in the details page"). All from the API's model (100-api common/model.py)
// on the pricing model chosen for the order, and the order's pricing model itself. Never names a
// data source.
//
//   RoofPlan            Section 7: roof and panel plan (the traced roof on the aerial photo)
//   SizingSection       Section 8: sizing the system (every size the roof holds)
//   RecommendedSystem   Section 9: the recommended system and its return (install cost, return, month by month)
//   RoiDetail           Section 9 continued: ROI in detail (cash position, cash flow, sensitivity)
//   BatteryCarbon       Section 9 continued: battery and carbon reporting
//   AssumptionsSection  Section 13: assumptions and method
//   PricingAppendix     Appendix: your pricing model (its prices, extras and assumptions)
import { useEffect, useState } from "react";
import { BatteryCharging, Calculator, ChartColumn, FileText, Info, LayoutGrid, PlugZap, SlidersHorizontal, Sun, Tag } from "lucide-react";
import { useApi, type OwnedBuildingDetail, type PackModelData } from "../api";
import { roofImagery } from "./BuildingMap";
import { KV } from "./EnergyOpportunity";
import { SectionHead } from "./PackSections";
import { RoofFacing } from "./SiteFacts";
import { cap, g, gbp, gbpShort, longDay, monthYear, mwh, num, occupant } from "./packFormat";

const yrs = (n: number | null | undefined) => (n == null ? "—" : `${n} yrs`);
const pct = (n: number | null | undefined) => (n == null ? "—" : `${n}%`);
const MONTHS = ["J", "F", "M", "A", "M", "J", "J", "A", "S", "O", "N", "D"];
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const W = 720;

/** 25-year value (bars) and payback (line) for every size; the recommended one in lime. */
function SizingChart({ m }: { m: PackModelData }) {
  const o = m.options;
  if (!o.length) return null;
  const H = 230,
    L = 56,
    R = 48,
    T = 12,
    B = 34;
  const maxV = Math.max(1, ...o.map((x) => x.npv)) * 1.1;
  const maxP = Math.max(1, ...o.map((x) => x.payback ?? 0)) * 1.15;
  const slot = (W - L - R) / o.length;
  const bw = Math.min(36, slot * 0.66);
  const y = (v: number) => T + (H - T - B) * (1 - Math.max(0, v) / maxV);
  const yp = (v: number) => T + (H - T - B) * (1 - v / maxP);
  const cx = (i: number) => L + slot * i + slot / 2;
  const line = o.map((x, i) => (x.payback == null ? null : `${cx(i)},${yp(x.payback)}`)).filter(Boolean);
  return (
    <svg className="eo-chart" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="25-year value and payback for each system size">
      {[0, 1 / 3, 2 / 3, 1].map((t) => (
        <g key={t}>
          <line x1={L} x2={W - R} y1={y(t * maxV)} y2={y(t * maxV)} className="eo-grid" />
          <text x={L - 6} y={y(t * maxV) + 4} className="eo-axis" textAnchor="end">
            {gbpShort(t * maxV)}
          </text>
          <text x={W - R + 6} y={yp(t * maxP) + 4} className="eo-axis">
            {Math.round(t * maxP)} yrs
          </text>
        </g>
      ))}
      {o.map((x, i) => (
        <g key={x.kwp}>
          <rect x={cx(i) - bw / 2} y={y(x.npv)} width={bw} height={y(0) - y(x.npv)} rx={3} className={x.kwp === m.recommended.kwp ? "eo-bar is-pick" : "eo-bar"}>
            <title>{`${x.kwp} kWp: value ${gbp(x.npv)}, payback ${yrs(x.payback)}`}</title>
          </rect>
          <text x={cx(i)} y={H - B + 14} className="eo-axis" textAnchor="middle">
            {x.kwp}
          </text>
        </g>
      ))}
      <polyline points={line.join(" ")} className="eo-line" />
      {o.map((x, i) => x.payback != null && <circle key={x.kwp} cx={cx(i)} cy={yp(x.payback)} r={3.5} className="eo-dot" />)}
      <text x={(W - R + L) / 2} y={H - 4} className="eo-axis" textAnchor="middle">
        System size (kWp)
      </text>
    </svg>
  );
}

/** Generation each month, used on site and exported, against the building's demand. */
function MonthlyChart({ m }: { m: PackModelData }) {
  const rows = m.recommended.monthly;
  if (!rows.length) return null;
  const H = 210,
    L = 56,
    T = 10,
    B = 24;
  const max = Math.max(...rows.map((r) => Math.max(r.gen, r.demand))) * 1.1;
  const slot = (W - L - 8) / 12;
  const bw = slot * 0.6;
  const y = (v: number) => T + (H - T - B) * (1 - v / max);
  const cx = (i: number) => L + slot * i + slot / 2;
  return (
    <svg className="eo-chart" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Generation each month against demand">
      {[0, 1 / 3, 2 / 3, 1].map((t) => (
        <g key={t}>
          <line x1={L} x2={W - 8} y1={y(t * max)} y2={y(t * max)} className="eo-grid" />
          <text x={L - 6} y={y(t * max) + 4} className="eo-axis" textAnchor="end">
            {((t * max) / 1000).toFixed(1)}
          </text>
        </g>
      ))}
      {rows.map((r, i) => (
        <g key={i}>
          <rect x={cx(i) - bw / 2} y={y(r.used)} width={bw} height={y(0) - y(r.used)} className="eo-bar" />
          <rect x={cx(i) - bw / 2} y={y(r.used + r.export)} width={bw} height={y(0) - y(r.export)} className="eo-export" />
          <text x={cx(i)} y={H - 6} className="eo-axis" textAnchor="middle">
            {MONTHS[i]}
          </text>
        </g>
      ))}
      <polyline points={rows.map((r, i) => `${cx(i)},${y(r.demand)}`).join(" ")} className="eo-line" />
      {rows.map((r, i) => (
        <circle key={i} cx={cx(i)} cy={y(r.demand)} r={3.5} className="eo-dot" />
      ))}
      <text x={6} y={T + 8} className="eo-axis">
        MWh
      </text>
    </svg>
  );
}

/** The cumulative cash position: install in year 0, then each year's net. */
function CashChart({ m }: { m: PackModelData }) {
  const rec = m.recommended;
  if (!rec.flows.length) return null;
  const pts = [{ year: 0, cum: -rec.capex }, ...rec.flows.map((f) => ({ year: f.year, cum: f.cum }))];
  const H = 220,
    L = 56,
    T = 14,
    B = 24;
  const hi = Math.max(1, ...pts.map((p) => p.cum)),
    lo = Math.min(0, ...pts.map((p) => p.cum));
  const y = (v: number) => T + (H - T - B) * ((hi - v) / (hi - lo));
  const slot = (W - L - 8) / pts.length;
  const bw = slot * 0.7;
  const cx = (i: number) => L + slot * i + slot / 2;
  return (
    <svg className="eo-chart" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Cumulative cash position over 25 years">
      <line x1={L} x2={W - 8} y1={y(0)} y2={y(0)} className="eo-zero" />
      {[lo, 0, hi / 2, hi].map((v) => (
        <text key={v} x={L - 6} y={y(v) + 4} className="eo-axis" textAnchor="end">
          {gbpShort(v)}
        </text>
      ))}
      {pts.map((p, i) => (
        <rect key={p.year} x={cx(i) - bw / 2} y={Math.min(y(p.cum), y(0))} width={bw} height={Math.abs(y(p.cum) - y(0))} className={p.cum < 0 ? "eo-neg" : "eo-bar"}>
          <title>{`Year ${p.year}: ${gbp(p.cum)}`}</title>
        </rect>
      ))}
      {rec.payback != null && (
        <>
          <line x1={cx(0) + slot * rec.payback} x2={cx(0) + slot * rec.payback} y1={T} y2={H - B} className="eo-divide" />
          <text x={cx(0) + slot * rec.payback + 4} y={T + 10} className="eo-axis">
            Payback {rec.payback} yrs
          </text>
        </>
      )}
      {[0, 5, 10, 15, 20, 25].map((yv) => (
        <text key={yv} x={cx(yv)} y={H - 6} className="eo-axis" textAnchor="middle">
          Yr {yv}
        </text>
      ))}
    </svg>
  );
}

/** Section 7: roof and panel plan: the traced roof on the aerial photo, and what it holds. */
export function RoofPlan({ b }: { b: OwnedBuildingDetail }) {
  const api = useApi();
  const sr = b.site?.roof;
  const m = b.energy?.model;
  const [img, setImg] = useState<{ imageUrl: string; shadeUrl?: string; date: string | null } | "none" | null>(null);
  useEffect(() => {
    let cancelled = false;
    roofImagery(api, b.orderId)
      .then((r) => !cancelled && setImg(r && "imageUrl" in r && r.imageUrl ? { imageUrl: r.imageUrl, shadeUrl: r.shadeUrl, date: r.date } : "none"))
      .catch(() => !cancelled && setImg("none"));
    return () => {
      cancelled = true;
    };
  }, [api, b.orderId]);
  if (!sr && !m) return null;
  const A = m?.assumptions;
  const R = m?.recommended;
  const when = img && img !== "none" ? monthYear(img.date) : "";
  return (
    <section className="card eo" aria-labelledby="pk-roofplan">
      <SectionHead
        id="pk-roofplan"
        kicker="Section 7"
        icon={LayoutGrid}
        title="Roof and panel plan"
        sub={
          <>
            The roof was mapped plane by plane and laid out with {num(sr?.maxPanels)} panel positions. Leaving {A?.roofLeftClearPct ?? b.energy?.system?.roofLeftClearPct}% of
            the roof clear (edges, rooflights, plant, walkways) gives room for up to {sr?.capacityKwp ?? "—"} kWp.
            {R ? ` The recommended ${R.kwp} kWp system uses ${num(R.panels)} of them.` : ""}
          </>
        }
      />
      {/* The aerial photo with the traced roof: everything else dimmed, so the roof stands out */}
      <figure className="rplan">
        {img === null ? (
          <div className="rplan-frame rplan-wait muted">Loading the roof imagery…</div>
        ) : img === "none" ? (
          <div className="rplan-frame rplan-wait muted">Roof imagery isn't available for this building.</div>
        ) : (
          <div className="rplan-frame">
            <img src={img.imageUrl} alt="Aerial view of the roof" />
            {img.shadeUrl && <img src={img.shadeUrl} alt="" aria-hidden="true" className="rplan-shade" />}
          </div>
        )}
        <p className="eo-legend muted">
          <span className="eo-key is-roof" /> The roof <span className="eo-key is-dimmed" /> Everything else, dimmed
        </p>
        <figcaption className="muted eo-note">
          Roof traced on aerial imagery{when ? ` (${when})` : ""}. Imagery includes Google data. Layout is indicative and must be confirmed by survey (structure,
          rooflights, fire access).
        </figcaption>
      </figure>
      <div className="eo-stats">
        <div>
          <b>{sr?.maxPanels != null ? num(sr.maxPanels) : "—"}</b>
          <span>panel positions found</span>
        </div>
        <div>
          <b>{sr?.capacityKwp != null ? `${sr.capacityKwp} kWp` : "—"}</b>
          <span>roof maximum with {A?.roofLeftClearPct ?? b.energy?.system?.roofLeftClearPct}% left clear</span>
        </div>
        <div>
          <b>{sr ? `${sr.flatPct}% flat` : "—"}</b>
          <span>{sr?.pitchRange ? `pitched ${sr.pitchRange[0]}–${sr.pitchRange[1]}°` : "panels can face south"}</span>
        </div>
        <div>
          <b>{sr?.sunnyPct != null ? `${sr.sunnyPct}%` : "—"}</b>
          <span>of the roof in strong sun</span>
        </div>
      </div>
      {/* The roof's aspect, sun and pitch next to the plan (the same card as the site section) */}
      <RoofFacing r={sr} />
    </section>
  );
}

/** Section 8: sizing the system. */
export function SizingSection({ b }: { b: OwnedBuildingDetail }) {
  const m = b.energy?.model;
  if (!m) return null;
  const rec = m.recommended;
  const occ = occupant(b.property.sector);
  const roofKwp = b.site?.roof?.capacityKwp ?? b.energy?.system?.roofCapKwp;
  return (
    <section className="card eo" aria-labelledby="pk-sizing">
      <SectionHead
        id="pk-sizing"
        kicker="Section 8"
        icon={SlidersHorizontal}
        title="Sizing the system"
        sub={`${roofKwp != null ? `The roof could take ${roofKwp} kWp. ` : ""}${m.profile.label}. We modelled ${m.options.length} sizes month by month against ${occ}'s demand.`}
      />
      <div className="eo-block">
        <SizingChart m={m} />
        <p className="eo-legend muted">
          <span className="eo-key is-actual" /> 25-year NPV <span className="eo-key is-pick" /> Recommended <span className="eo-key is-central" /> Payback (right axis)
        </p>
      </div>
      <div className="eo-block">
        <p className="muted eo-note">
          Output: generation a year. Self-use: share used on site. Covers: share of {occ}'s electricity met by solar. Year 1: bill saving plus export income. ★
          recommended.
        </p>
        <div className="eo-table-wrap">
          <table className="eo-table">
            <thead>
              <tr>
                <th>Size</th>
                <th>Panels</th>
                <th>Output</th>
                <th>Self-use</th>
                <th>Covers</th>
                <th>Install</th>
                <th>Year 1</th>
                <th>Payback</th>
                <th>IRR</th>
                <th>NPV</th>
              </tr>
            </thead>
            <tbody>
              {m.options.map((o) => (
                <tr key={o.kwp} className={o.kwp === rec.kwp ? "is-pick" : undefined}>
                  <td>
                    {o.kwp} kWp{o.kwp === rec.kwp ? " ★" : ""}
                  </td>
                  <td>{num(o.panels)}</td>
                  <td>{mwh(o.gen)}</td>
                  <td>{o.selfUse}%</td>
                  <td>{o.covers}%</td>
                  <td>{gbp(o.capex)}</td>
                  <td>{gbp(o.year1)}</td>
                  <td>{yrs(o.payback)}</td>
                  <td>{pct(o.irr)}</td>
                  <td>{gbp(o.npv)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="eo-callouts">
        <p className="eo-callout">
          <b>Recommended: {rec.kwp} kWp.</b> The highest 25-year value of any size paying back within {m.assumptions.maxPayback} years: {gbp(rec.npv)} NPV,{" "}
          {rec.payback} years, covering {rec.covers}% of {occ}'s electricity.
        </p>
        {m.phase1 && (
          <p className="eo-callout is-dark">
            <b>Phase 1 option: {m.phase1.kwp} kWp.</b> The best return per pound ({m.phase1.irr}% IRR, {m.phase1.payback}-year payback) for {gbp(m.phase1.capex)}, with
            room to add more later.
          </p>
        )}
      </div>
    </section>
  );
}

/** Section 9: the recommended system and its return. */
export function RecommendedSystem({ b }: { b: OwnedBuildingDetail }) {
  const m = b.energy?.model;
  if (!m) return null;
  const rec = m.recommended;
  const A = m.assumptions;
  const occ = occupant(b.property.sector);
  const modelName = b.energy?.pricingModel || "Our standard prices";
  return (
    <section className="card eo" aria-labelledby="pk-system">
      <SectionHead id="pk-system" kicker="Section 9" icon={Sun} title="The recommended system and its return" sub={`${rec.kwp} kWp, ${num(rec.panels)} panels, ${mwh(rec.gen)} a year.`} />
      <div className="pm-two">
        <div className="eo-block">
          <h3>
            <Calculator size={16} aria-hidden="true" className="pk-h3-ico" /> Install cost (ex VAT)
          </h3>
          <table className="eo-table eo-capex">
            <tbody>
              {rec.items.map((i) => (
                <tr key={i.label}>
                  <td>{i.label}</td>
                  <td>{gbp(i.amount)}</td>
                </tr>
              ))}
              <tr className="eo-total">
                <td>Total</td>
                <td>{gbp(rec.capex)}</td>
              </tr>
            </tbody>
          </table>
          <p className="muted eo-note">
            £/kWp band pricing with extras, on {modelName.startsWith("Our ") ? modelName.toLowerCase() : modelName}. A quotation will follow survey. Price range ±10%:{" "}
            {gbp(rec.priceRange[0])}–{gbp(rec.priceRange[1])}.
          </p>
        </div>
        <div className="eo-block">
          <h3>Headline return</h3>
          <div className="eo-stats is-two">
            <div className="is-dark">
              <b>{yrs(rec.payback)}</b>
              <span>simple payback</span>
            </div>
            <div className="is-dark">
              <b>{pct(rec.irr)}</b>
              <span>IRR (25 years)</span>
            </div>
            <div>
              <b>{gbp(rec.npv)}</b>
              <span>NPV at {Math.round(A.discountPct)}%</span>
            </div>
            <div>
              <b>{rec.lcoeP != null ? `${rec.lcoeP}p` : "—"}</b>
              <span>cost per kWh generated (vs {A.importPence}p bought)</span>
            </div>
            <div>
              <b>{gbpShort(rec.netBenefit)}</b>
              <span>net benefit over 25 years</span>
            </div>
            <div>
              <b>{num(rec.co2Life)} t</b>
              <span>CO₂ avoided over 25 years</span>
            </div>
          </div>
        </div>
      </div>
      <div className="eo-block">
        <h3>
          <Sun size={16} aria-hidden="true" className="pk-h3-ico" /> Month by month: generation vs {occ}'s demand
        </h3>
        <MonthlyChart m={m} />
        <p className="eo-legend muted">
          <span className="eo-key is-actual" /> Solar used on site <span className="eo-key is-range" /> Solar exported <span className="eo-key is-central" /> {cap(occ)}'s
          demand
        </p>
        <p className="muted eo-note">
          {num(rec.gen)} kWh a year generated; {num(rec.used)} kWh ({rec.selfUse}%) used on site, {rec.covers}% of {occ}'s electricity.
        </p>
      </div>
    </section>
  );
}

/** Section 9 continued: ROI in detail. */
export function RoiDetail({ b }: { b: OwnedBuildingDetail }) {
  const m = b.energy?.model;
  if (!m) return null;
  const rec = m.recommended;
  const shown = [1, 3, 5, 7, 10, 13, 15, 20, 25];
  // The row the system pays back in, highlighted (as the report)
  const paybackYear = Math.round(rec.payback ?? 0.5);
  return (
    <section className="card eo" aria-labelledby="pk-roi">
      <SectionHead id="pk-roi" kicker="Section 9 continued" icon={Calculator} title="ROI in detail" />
      <div className="eo-block">
        <CashChart m={m} />
        <p className="muted eo-note">
          Cumulative cash position: install cost in year 0, then each year's savings and export income, less O&amp;M and the inverter replacement in year{" "}
          {m.assumptions.inverterYear}.
        </p>
      </div>
      <div className="eo-block">
        <h3>
          <ChartColumn size={16} aria-hidden="true" className="pk-h3-ico" /> 25-year cash flow (selected years)
        </h3>
        <div className="eo-table-wrap">
          <table className="eo-table">
            <thead>
              <tr>
                <th>Year</th>
                <th>Generation kWh</th>
                <th>Bill saving</th>
                <th>Export income</th>
                <th>O&amp;M / inverter</th>
                <th>Net</th>
                <th>Cumulative</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Year 0</td>
                <td />
                <td />
                <td />
                <td />
                <td className="eo-negtext">{gbp(-rec.capex)}</td>
                <td className="eo-negtext">{gbp(-rec.capex)}</td>
              </tr>
              {rec.flows
                .filter((f) => shown.includes(f.year))
                .map((f) => (
                  <tr key={f.year} className={f.year === paybackYear ? "is-pick" : undefined}>
                    <td>Year {f.year}</td>
                    <td>{num(f.gen)}</td>
                    <td>{gbp(f.saving)}</td>
                    <td>{gbp(f.export)}</td>
                    <td>{gbp(-(f.om + f.inverter))}</td>
                    <td>{gbp(f.net)}</td>
                    <td className={f.cum < 0 ? "eo-negtext" : undefined}>{gbp(f.cum)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="eo-block">
        <h3>
          <SlidersHorizontal size={16} aria-hidden="true" className="pk-h3-ico" /> Sensitivity: what moves the answer
        </h3>
        <div className="eo-table-wrap">
          <table className="eo-table">
            <thead>
              <tr>
                <th>Scenario</th>
                <th>Payback</th>
                <th>IRR</th>
                <th>NPV</th>
              </tr>
            </thead>
            <tbody>
              {m.sensitivity.map((s, i) => (
                <tr key={s.label} className={i === 0 ? "is-pick" : undefined}>
                  <td>{s.label}</td>
                  <td>{yrs(s.payback)}</td>
                  <td>{pct(s.irr)}</td>
                  <td>{gbp(s.npv)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

/** Section 9 continued: a battery, and carbon reporting. Only when the model worked either out. */
export function BatteryCarbon({ b }: { b: OwnedBuildingDetail }) {
  const rec = b.energy?.model?.recommended;
  const bat = rec?.battery;
  const carb = rec?.carbon;
  if (!rec || (!bat && !carb)) return null;
  const comb = bat?.combined;
  return (
    <section className="card eo" aria-labelledby="pk-battery">
      <SectionHead id="pk-battery" kicker="Section 9 continued" icon={BatteryCharging} title="Battery and carbon reporting" />
      <div className="eo-block">
        <h3>Add a battery?</h3>
        {bat?.kwh ? (
          <>
            <div className="eo-stats">
              <div className={bat.recommended ? "is-dark" : undefined}>
                <b>{bat.kwh} kWh</b>
                <span>{bat.recommended ? "recommended battery" : "battery sized, not recommended"}</span>
              </div>
              <div>
                <b>{gbp(bat.cost)}</b>
                <span>battery cost, ex VAT</span>
              </div>
              <div>
                <b>{gbp(bat.savingYear1)}</b>
                <span>extra saved in year one</span>
              </div>
              <div>
                <b>{bat.selfUseWith}%</b>
                <span>of the solar used on site (from {rec.selfUse}%)</span>
              </div>
            </div>
            <KV
              rows={[
                ["Battery payback", bat.payback != null ? `${bat.payback} years` : "—"],
                ["Solar and battery together", comb ? `${gbp(comb.capex)}, payback ${comb.payback ?? "—"} years, ${comb.irr ?? "—"}% IRR` : null],
                ["25-year value", comb ? `${gbp(comb.npv)} with the battery, ${gbp(rec.npv)} solar alone` : null],
                ["Electricity stored and used", bat.shiftedKwh != null ? `${num(bat.shiftedKwh)} kWh a year` : null],
                ["Replacement", bat.replacementYear ? `Once, in year ${bat.replacementYear}, at ${bat.replacementPct}% of today's cost` : null],
              ]}
            />
          </>
        ) : null}
        <p className="eo-note">{bat?.why || "A battery option was not worked out for this report."}</p>
      </div>
      <div className="eo-block">
        <h3>Carbon reporting</h3>
        {carb ? (
          <>
            <div className="eo-stats">
              <div className="is-dark">
                <b>{carb.co2Year} t</b>
                <span>CO₂e avoided a year</span>
              </div>
              <div className="is-dark">
                <b>{carb.scope2Pct}%</b>
                <span>of the building's Scope 2 electricity emissions</span>
              </div>
              <div>
                <b>{num(carb.co2Life)} t</b>
                <span>CO₂e over 25 years</span>
              </div>
              <div>
                <b>{carb.factorKgPerKwh} kg</b>
                <span>CO₂e a kWh used</span>
              </div>
            </div>
            <p className="muted eo-note">{carb.note}</p>
          </>
        ) : (
          <p className="pk-notfound">Carbon figures weren't worked out for this report.</p>
        )}
        <p className="muted eo-note">
          For the owner's sustainability lead: the figures they report each year (SECR, net-zero targets) fall by the share above once the system runs.
        </p>
      </div>
    </section>
  );
}

/** Section 13: assumptions and method. */
export function AssumptionsSection({ b }: { b: OwnedBuildingDetail }) {
  const m = b.energy?.model;
  if (!m) return null;
  const A = m.assumptions;
  const rec = m.recommended;
  const occ = occupant(b.property.sector);
  const profile = m.profile.label;
  const modelName = b.energy?.pricingModel || "Our standard prices";
  return (
    <section className="card eo" aria-labelledby="pk-method">
      <SectionHead id="pk-method" kicker="Section 13" icon={FileText} title="Assumptions and method" />
      <div className="about-two">
        <div className="pk-panel">
          <h3 className="pk-sub">Prices</h3>
          <KV
            rows={[
              ["Electricity import", `${A.importPence}p/kWh, rising ${A.inflationPct}% a year`],
              ["Export", `${A.exportPence}p/kWh, flat (Smart Export Guarantee or PPA)`],
              ["Pricing model", modelName],
            ]}
          />
          <h3 className="pk-sub pk-sub-gap">System</h3>
          <KV
            rows={[
              ["Panels", A.panel],
              ["Yield", `${A.yieldKwhPerKwp} kWh per kWp a year after ${A.lossesPct}% losses`],
              ["Degradation", `${A.degradationPct}% a year`],
              ["Roof left clear", `${A.roofLeftClearPct}%`],
              ["O&M and monitoring", `£${A.omPerKwp}/kWp a year, rising with inflation`],
              ["Inverter replacement", `£${Math.round(A.inverterPerKwp)}/kWp in year ${A.inverterYear}`],
            ]}
          />
          <h3 className="pk-sub pk-sub-gap">Finance</h3>
          <KV
            rows={[
              ["Discount rate (NPV)", `${Math.round(A.discountPct)}%`],
              ["Life", `${A.years} years`],
              ["Grid CO₂", `${A.co2KgPerKwh} kg/kWh`],
            ]}
          />
        </div>
        <div className="pk-panel pk-prose">
          <h3 className="pk-sub">
            <PlugZap size={16} aria-hidden="true" /> How self-consumption is modelled
          </h3>
          <p>
            Each month, generation follows a typical UK solar profile (June about 13% of the year, December about 2%). Demand follows the building's opening pattern (
            {profile.toLowerCase()}): solar meets the daylight load first, and the rest is exported.
          </p>
          <p>Half-hourly meter data from {occ} would replace this model at survey stage.</p>
          <h3 className="pk-sub">
            <Info size={16} aria-hidden="true" /> How the figures were produced
          </h3>
          {/* The energy figures' basis in neutral words: records, or an estimate from size and use (private) */}
          <p>
            From{" "}
            {b.energy?.basis === "benchmark"
              ? "an estimate of the building's electricity use from its size and use"
              : b.energy?.estimated
                ? "the building's energy records, with an estimated latest reading"
                : "the building's actual energy records"}
            , a scan of its roof, its local grid and planning information, public information checked by our team, and the pricing model chosen for this order.
          </p>
          <p>
            This report combines open data (including government open data and openly licensed community data), data from third parties, data licensed to us, and our
            own curated and derived data, with our analysis and AI checks.
          </p>
        </div>
      </div>
      {rec.monthly.length > 0 && (
        <div className="eo-block">
          <h3>
            <ChartColumn size={16} aria-hidden="true" className="pk-h3-ico" /> Monthly model for the recommended {rec.kwp} kWp system (MWh)
          </h3>
          <div className="eo-table-wrap">
            <table className="eo-table">
              <thead>
                <tr>
                  <th />
                  {MONTH_NAMES.map((mo) => (
                    <th key={mo}>{mo}</th>
                  ))}
                  <th>Year</th>
                </tr>
              </thead>
              <tbody>
                {(
                  [
                    ["Demand", "demand"],
                    ["Generated", "gen"],
                    ["Used on site", "used"],
                    ["Exported", "export"],
                  ] as const
                ).map(([label, key]) => (
                  <tr key={key}>
                    <td>{label}</td>
                    {rec.monthly.map((r, i) => (
                      <td key={i}>{(r[key] / 1000).toFixed(1)}</td>
                    ))}
                    <td>
                      <b>{(rec.monthly.reduce((a, r) => a + r[key], 0) / 1000).toFixed(0)}</b>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <div className="pk-callout">
        <Info size={16} aria-hidden="true" />
        <span>
          <b>Important.</b> This Solar Opportunity Report is for prospecting, not a quotation or design. Panel layout, structure, fire access, grid capacity and prices
          must be confirmed by survey. Contact details are from public sources and should be verified before use; outreach must follow PECR and UK GDPR.
        </span>
      </div>
    </section>
  );
}

// The parts of an itemised band, in the report's order
const PART_NAMES: [keyof NonNullable<NonNullable<NonNullable<OwnedBuildingDetail["energy"]>["pricing"]>["bands"][number]["parts"]>, string][] = [
  ["panels", "Panels"],
  ["inverters", "Inverters"],
  ["mounting", "Mounting"],
  ["electrical", "Electrical"],
  ["labour", "Labour"],
  ["margin", "Margin"],
];

/** Appendix: the pricing model behind every figure, as the order keeps it (Neil, 10 Oct 2026). */
export function PricingAppendix({ b }: { b: OwnedBuildingDetail }) {
  const pm = b.energy?.pricing;
  if (!pm) return null;
  const A = b.energy?.model?.assumptions;
  const R = b.energy?.model?.recommended;
  const modelName = b.energy?.pricingModel || "Our standard prices";
  const applied = b.pricingStatus?.applied;
  // When the model was put on this property: its last recalculation, else the order
  const on = applied?.appliedAt || b.orderedAt;
  const bands = pm.bands ?? [];
  const lower = [0, ...bands.slice(0, -1).map((x) => x.upToKwp)];
  const acc = pm.access;
  const access =
    {
      scaffold: `Scaffolding at £${num(acc?.scaffoldPerM)} a metre of roof edge`,
      mewp: `Access platform at £${num(acc?.mewpPerDay)} a day, ${g(acc?.kwpPerDay)} kWp installed a day`,
      fixed: `Fixed £${num(acc?.fixed)}`,
      none: "None",
    }[acc?.mode ?? "none"] ?? "";
  return (
    <section className="card eo" aria-labelledby="pk-pricing">
      <SectionHead
        id="pk-pricing"
        kicker="Appendix"
        icon={Tag}
        title="Your pricing model"
        sub={
          <>
            The prices and assumptions behind every figure in this report:{" "}
            <b>
              {modelName}
              {applied?.version ? ` v${applied.version}` : ""}
            </b>
            {longDay(on) ? `, applied to this property on ${longDay(on)}` : ""}.
          </>
        }
      />
      <div className="eo-block">
        <h3>Installed price by system size</h3>
        <div className="eo-table-wrap">
          <table className="eo-table">
            <thead>
              {pm.itemised ? (
                <tr>
                  <th>System size</th>
                  {PART_NAMES.map(([, n]) => (
                    <th key={n}>{n}</th>
                  ))}
                  <th>£/kWp</th>
                </tr>
              ) : (
                <tr>
                  <th>System size</th>
                  <th>Installed price, £/kWp ex VAT</th>
                </tr>
              )}
            </thead>
            <tbody>
              {bands.map((band, i) => (
                <tr key={band.upToKwp}>
                  <td>
                    {g(lower[i])}–{g(band.upToKwp)} kWp
                  </td>
                  {pm.itemised && PART_NAMES.map(([k]) => <td key={k}>£{num(band.parts?.[k] ?? 0)}</td>)}
                  <td>
                    <b>£{num(band.perKwp)}</b>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="muted eo-note">
          A system is priced at the band its size falls in{pm.itemised ? "; each band is the sum of its parts" : ""}.{R ? ` The recommended ${R.kwp} kWp system is itemised in Section 9.` : ""}
        </p>
      </div>
      <div className="about-two">
        <div className="pk-panel">
          <h3 className="pk-sub">Extras</h3>
          <KV
            rows={[
              ["Roof access", access],
              ["Structural survey", `£${num(pm.structuralSurvey)}`],
              ["Design", `£${num(pm.designFee)}`],
              ["G99 grid application", `£${num(pm.g99Application)}`],
              ["G99 witness test", `£${num(pm.g99WitnessTest)}, for systems over ${g(pm.g99WitnessKw)} kW`],
              ["Monitoring", `£${num(pm.monitoring)}`],
            ]}
          />
        </div>
        <div className="pk-panel">
          <h3 className="pk-sub">Assumptions</h3>
          <KV
            rows={[
              ["Roof left clear", `${g(pm.roofLeftClearPct)}% (edges, rooflights, plant, walkways)`],
              ["Panels", `${pm.panelModel ?? ""} (${g(pm.panelWatts)} W)`],
              [
                "Electricity bought",
                `${g(pm.importPence)}p/kWh, rising ${g(pm.priceInflationPct)}% a year` +
                  (A?.importSource === "your rate for this property" ? ` (this property's own rate, ${A.importPence}p/kWh, is used)` : ""),
              ],
              ["Electricity exported", `${g(pm.exportPence)}p/kWh`],
              ["System losses", `${g(pm.systemLossesPct)}% (inverter, cabling, soiling)`],
              ["Maintenance and monitoring", `£${g(pm.omPerKwp)}/kWp a year`],
              [
                "Battery",
                pm.batteryPerKwh
                  ? `£${num(pm.batteryPerKwh)}/kWh installed, ${g(pm.batteryRoundTripPct)}% round trip, ${g(pm.batteryCyclesPerYear)} cycles a year`
                  : null,
              ],
              ["VAT", `${g(pm.vatPct)}% (prices in this report are ex VAT)`],
            ]}
          />
        </div>
      </div>
      <div className="pk-callout">
        <Tag size={16} aria-hidden="true" />
        <span>
          Changing a pricing model doesn't change this report: the property keeps the version above until someone recalculates it on another model or a newer version,
          from its page.
        </span>
      </div>
    </section>
  );
}
